/*
  <capra-gallery>: the product photo strip (snippets/product-media.liquid).
  Without this script it is a sideways scroll and each photo links to its
  full-size file. With it:
    - arrows step photo by photo, in a loop (after the last, the first); on desktop they show while the mouse moves
      over the photos and fade when it rests; the arrow keys step too, from the page as well as the strip, and
      Home and End jump to the ends (keys()); a swipe past the last photo (or the first) loops the same way, and a visitor
      who has swiped never sees the arrows again (owner, 2026-10-22);
    - the mouse drags the strip sideways (a drag never opens a photo);
    - a click opens the full-screen viewer (capra-viewer.js and every photo at full
      size load when the pointer, a touch or focus first reaches the photos, never at page load);
    - a vertical video plays itself, silent, while on screen (capra-film.js); wide ones are in the details slideshow;
    - every photo loads at once on the first touch, pointer or focus, so an arrow never reveals an
      empty box (on a good line they already load once the first has arrived: layout/theme.liquid);
    - with a mouse, a frosted glass loupe follows the pointer over the photos and in the viewer;
    - browsing the photos (an arrow, a key, a drag or a swipe) is immersive: the header slides
      away and the buy bar dims (:root.is-immersive) until the page scrolls on or the photos
      leave the screen.
*/

class CapraGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    if (!this.track) return;
    this.items = [...this.track.children];
    this.prev = this.querySelector('[data-prev]');
    this.next = this.querySelector('[data-next]');
    this.swiped = false;
    try { this.swiped = localStorage.getItem('capra:swiped') === '1'; } catch { /* private window: the arrows show */ }
    for (const arrow of [this.prev, this.next]) if (arrow) arrow.hidden = this.swiped;
    this.prev?.addEventListener('click', () => this.step(-1));
    this.next?.addEventListener('click', () => this.step(1));
    this.track.addEventListener('scroll', () => this.update(), { passive: true });
    this.keys();
    addEventListener('resize', () => this.update());
    this.update();
    this.arrowsOnMove();
    this.drag();
    this.swipe();
    this.track.addEventListener('click', (event) => this.view(event));
    // The first touch, pointer or focus on the photos fetches the viewer and every photo at full
    // size, so the first click opens at once and no photo is still arriving inside it.
    const warm = () => this.prepare();
    for (const type of ['pointerenter', 'pointerdown', 'focusin']) this.addEventListener(type, warm, { once: true, passive: true });
    this.warm();
    this.immersive();
    this.loupe();
  }

  // The loupe (owner, 2026-10-19): with a mouse, a frosted glass disc follows the pointer over the
  // photos (a plus) and in the viewer (a minus), in the same glass as the film's play button. One disc
  // in the strip and one in the viewer's dialog, so it stays above the photos in each. Without this
  // script, or with touch, the drawn cursor stays (tokens.css, --cursor-zoom-in).
  loupe() {
    if (!matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    const dialog = this.querySelector('[data-viewer]');
    const disc = (host, out) => host.appendChild(Object.assign(document.createElement('span'), { className: `loupe${out ? ' loupe--out' : ''}`, ariaHidden: 'true' }));
    const follow = (host, selector, out) => {
      const glass = disc(host, out);
      let frame = 0;
      host.addEventListener('pointermove', (event) => {
        const on = event.pointerType === 'mouse' && Boolean(event.target.closest(selector));
        glass.classList.toggle('is-on', on);
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => { glass.style.translate = `${event.clientX}px ${event.clientY}px`; });
      }, { passive: true });
      host.addEventListener('pointerleave', () => glass.classList.remove('is-on'));
    };
    this.classList.add('has-loupe');
    follow(this.track, '.gallery__link', false);
    if (dialog) follow(dialog, '.viewer__item', true);
  }

  // Every photo on the first sign of browsing: lazy loading left the next photo blank
  warm() {
    const all = () => {
      for (const img of this.track.querySelectorAll('img[loading="lazy"]')) img.loading = 'eager';
    };
    for (const type of ['pointerenter', 'pointerdown', 'focusin']) this.addEventListener(type, all, { once: true, passive: true });
  }

  // While the visitor browses the photos, nothing else on the page asks for attention
  immersive() {
    const root = document.documentElement;
    let fromY = 0;
    let lastLeft = this.track.scrollLeft;
    const bar = document.querySelector('.buybar');
    const enter = () => {
      if (root.classList.contains('is-immersive')) return;
      // The header leaves its place: the page settles so the photos meet the top edge, no strip of page above
      const top = Math.round(this.getBoundingClientRect().top + scrollY);
      fromY = top;
      root.classList.add('is-immersive');
      if (Math.abs(scrollY - top) > 1) scrollTo({ top, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    };
    const leave = () => {
      root.classList.remove('is-immersive');
      bar?.classList.remove('is-near');
    };
    // The dimmed bar wakes as the pointer approaches it, so it is ready when the pointer arrives
    let frame = 0;
    addEventListener('pointermove', (event) => {
      if (!bar || frame || !root.classList.contains('is-immersive')) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const box = bar.querySelector('form')?.getBoundingClientRect() || bar.getBoundingClientRect();
        const near = event.clientY > box.top - 140 && event.clientX > box.left - 140 && event.clientX < box.right + 140;
        bar.classList.toggle('is-near', near);
      });
    }, { passive: true });
    for (const arrow of [this.prev, this.next]) arrow?.addEventListener('click', enter);
    this.addEventListener('capra:keystep', enter);
    // A swipe or a drag: a real sideways move of the strip
    this.track.addEventListener('scroll', () => {
      if (Math.abs(this.track.scrollLeft - lastLeft) > 24) enter();
      lastLeft = this.track.scrollLeft;
    }, { passive: true });
    addEventListener('scroll', () => { if (Math.abs(scrollY - fromY) > 80) leave(); }, { passive: true });
    new IntersectionObserver(([entry]) => { if (!entry.isIntersecting) leave(); }, { threshold: 0.4 }).observe(this);
  }

  // The arrow keys step the photos, and Home and End go to the first and last. Inside the gallery (the
  // strip, a photo, an arrow) always; from the page itself (nothing focused, as after a mouse click) the
  // arrows step too while the photos are on screen, so a mouse visitor need not first tab into the strip.
  // Never from a field, a menu or an open dialog, and never with a modifier (Alt+Left is Back).
  keys() {
    let seen = false;
    new IntersectionObserver(([entry]) => { seen = entry.isIntersecting; }, { threshold: 0.5 }).observe(this.track);
    document.addEventListener('keydown', (event) => {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      const at = document.activeElement;
      const inside = this.contains(at) && !at.closest('[data-viewer]');
      const page = (!at || at === document.body) && seen && !document.querySelector('dialog[open]');
      if (!inside && !page) return;
      const move = { ArrowRight: () => this.step(1), ArrowLeft: () => this.step(-1),
        Home: inside && (() => this.track.scrollTo({ left: 0 })),
        End: inside && (() => this.track.scrollTo({ left: this.track.scrollWidth })) }[event.key];
      if (!move) return;
      event.preventDefault();
      move();
      this.dispatchEvent(new Event('capra:keystep'));
    });
  }

  // A loop: the arrows never switch off; there is only a strip too short to scroll.
  update() {
    const scrolls = this.track.scrollWidth - this.track.clientWidth > 2;
    for (const arrow of [this.prev, this.next]) if (arrow) arrow.disabled = !scrolls;
  }

  // To the next photo whose left edge is past the current view; after the last comes the first, and back.
  step(direction) {
    const left = this.track.scrollLeft;
    const max = this.track.scrollWidth - this.track.clientWidth;
    if (direction > 0 && left >= max - 2) return this.track.scrollTo({ left: 0 });
    if (direction < 0 && left <= 2) return this.track.scrollTo({ left: max });
    const target = direction > 0
      ? this.items.find((item) => item.offsetLeft > left + 4)
      : this.items.findLast((item) => item.offsetLeft < left - 4);
    this.track.scrollTo({ left: target ? Math.min(target.offsetLeft, max) : direction > 0 ? max : 0 });
  }

  arrowsOnMove() {
    let rest;
    this.addEventListener('pointermove', (event) => {
      if (event.pointerType !== 'mouse') return;
      this.classList.add('is-pointing');
      clearTimeout(rest);
      rest = setTimeout(() => {
        if (!this.querySelector('.gallery__arrow:hover')) this.classList.remove('is-pointing');
      }, 1600);
    });
    this.addEventListener('pointerleave', () => {
      clearTimeout(rest);
      this.classList.remove('is-pointing');
    });
  }

  // A finger: a swipe past either end loops (the arrows do), and the first real swipe retires the arrows for good
  swipe() {
    let from = null;
    this.track.addEventListener('touchstart', (event) => {
      const [touch] = event.touches;
      const max = this.track.scrollWidth - this.track.clientWidth;
      from = event.touches.length === 1 && !event.target.closest('.film__bar')
        ? { x: touch.clientX, y: touch.clientY, first: this.track.scrollLeft <= 2, last: this.track.scrollLeft >= max - 2, max }
        : null;
    }, { passive: true });
    this.track.addEventListener('touchend', (event) => {
      if (!from) return;
      const [touch] = event.changedTouches;
      const dx = touch.clientX - from.x;
      const dy = touch.clientY - from.y;
      const start = from;
      from = null;
      if (Math.abs(dx) < 30 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
      this.retireArrows();
      if (start.max <= 2) return;
      // The strip did not move under a swipe at an end: go round
      if (dx < 0 && start.last) this.track.scrollTo({ left: 0 });
      else if (dx > 0 && start.first) this.track.scrollTo({ left: start.max });
    }, { passive: true });
    this.track.addEventListener('touchcancel', () => { from = null; }, { passive: true });
  }

  retireArrows() {
    if (this.swiped) return;
    this.swiped = true;
    for (const arrow of [this.prev, this.next]) if (arrow) arrow.hidden = true;
    try { localStorage.setItem('capra:swiped', '1'); } catch { /* it lasts for this visit */ }
  }

  drag() {
    let drag = null;
    this.track.addEventListener('dragstart', (event) => event.preventDefault()); // Firefox drags the image otherwise
    this.track.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button === 0) drag = { x: event.clientX, left: this.track.scrollLeft, moved: false };
    });
    addEventListener('pointermove', (event) => {
      if (!drag) return;
      const dx = event.clientX - drag.x;
      if (!drag.moved && Math.abs(dx) > 6) {
        drag.moved = true;
        this.track.classList.add('is-dragging');
      }
      if (drag.moved) this.track.scrollLeft = drag.left - dx;
    });
    addEventListener('pointerup', () => {
      if (!drag) return;
      this.dragged = drag.moved; // the click that ends a drag must not open the viewer
      drag = null;
      this.track.classList.remove('is-dragging');
      setTimeout(() => { this.dragged = false; }, 0);
    });
  }

  async view(event) {
    const link = event.target.closest('[data-view]');
    const dialog = this.querySelector('[data-viewer]');
    if (!link || !dialog || typeof dialog.showModal !== 'function') return;
    event.preventDefault();
    if (this.dragged) return;
    await this.prepare();
    this.viewer.open(Number(link.dataset.view), event.detail ? event.clientX : 0, event.detail ? event.clientY : 0);
  }

  prepare() {
    // The versioned URL from Liquid: a bare relative import would keep an old copy after a theme update.
    this.ready ||= import(this.dataset.viewerSrc || new URL('./capra-viewer.js', import.meta.url)).then(({ Viewer }) => {
      const dialog = this.querySelector('[data-viewer]');
      if (!dialog) return;
      this.viewer ||= new Viewer(dialog, this.track);
      this.viewer.preload();
    });
    return this.ready;
  }
}

customElements.define('capra-gallery', CapraGallery);
